<template>
  <div>
    <DxDataGrid
      ref="worksGrid"
      :data-source="worksDataSource"
      :selected-row-keys="selectedWorkKeys"
      :column-min-width="110"
      :show-column-lines="true"
      :show-borders="true"
      :column-auto-width="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      @content-ready="onContentReady"
      @selection-changed="onSelectionChanged"
      @row-click="onRowClick"
      @option-changed="onOptionChanged"
    >
      <DxSelection mode="multiple" showCheckBoxesMode="always" selectAllMode="page" />
      <DxFilterRow :visible="true" />
      <DxHeaderFilter :visible="false" />

      <DxColumn
        data-field="approval_status"
        :caption="$t('works.work_approval.approval_status')"
        :fixed="false"
        fixed-position="right"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        cell-template="approvalTemplate"
        :calculate-cell-value="getApprovalStatusText"
        :width="130"
      >
        <DxLookup :data-source="approvalOptions" value-expr="value" display-expr="name" />
      </DxColumn>
      <template #approvalTemplate="{ data }">
        <span :class="['approval-badge', getApprovalStatusClass(data.data)]">
          {{ getApprovalStatusText(data.data) }}
        </span>
      </template>

      <DxColumn
        data-field="row_type"
        :caption="$t('common.type')"
        :width="130"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        cell-template="rowTypeTemplate"
        :calculate-cell-value="getRowTypeColumnText"
      >
        <DxLookup :data-source="rowTypeOptions" value-expr="value" display-expr="name" />
      </DxColumn>
      <template #rowTypeTemplate="{ data }">
        <span :class="['row-type-badge', data.data.row_type]">
          {{ getRowTypeColumnText(data.data) }}
        </span>
      </template>

      <DxColumn
        data-field="public_id"
        :caption="$t('works.work_approval.public_id')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="work_date"
        :caption="$t('works.work_approval.work_date')"
        data-type="date"
        format="yyyy-MM-dd"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="legal_entity_name"
        :caption="$t('works.work_approval.legal_entity_name')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="unit_name"
        :caption="$t('works.work_approval.unit_name')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="parcel_name"
        :caption="$t('works.work_approval.parcel_name')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="parcel_settlement"
        :caption="$t('works.work_approval.parcel_settlement')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="parcel_surface"
        :caption="$t('works.work_approval.parcel_surface')"
        data-type="number"
        format="#,##0.#### ha"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="culture_name"
        :caption="$t('works.work_approval.culture_name')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="season_uuid"
        :caption="$t('works.work_approval.season_name')"
        :width="170"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        cell-template="seasonViewTemplate"
        :calculate-cell-value="getSeasonColumnText"
      >
        <DxLookup :data-source="seasonsList" value-expr="season_uuid" display-expr="name" />
      </DxColumn>
      <template #seasonViewTemplate="{ data }">
        <span>
          {{ getSeasonColumnText(data.data) }}
        </span>
      </template>
      <DxColumn
        data-field="work_uuid"
        :caption="$t('works.work_approval.work_name')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        cell-template="workTypeViewTemplate"
        :calculate-cell-value="getWorkTypeColumnText"
      >
        <DxLookup :data-source="workTypes" value-expr="uuid" display-expr="name" />
      </DxColumn>
      <template #workTypeViewTemplate="{ data }">
        <span>
          {{ getWorkTypeColumnText(data.data) }}
        </span>
      </template>
      <DxColumn
        data-field="surface_worked"
        :caption="$t('works.work_approval.surface_worked')"
        data-type="number"
        format="#,##0.#### ha"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="cost_work"
        :caption="$t('works.work_approval.cost_work')"
        data-type="number"
        format="#,##0.##"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="person_name"
        :caption="$t('works.work_approval.labor_force')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="work_duration_hours"
        :caption="$t('works.work_approval.duration_hours')"
        data-type="number"
        format="#,##0.##"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="machines"
        :caption="$t('works.work_approval.machines')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="fuel_consumption"
        :caption="$t('works.work_approval.fuel_consumption')"
        data-type="number"
        format="#,##0.##"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="consum_id"
        :caption="$t('works.work_approval.consum_id')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="document_number"
        :caption="$t('works.work_approval.document_number')"
        :allow-sorting="false"
        :allow-filtering="false"
        cell-template="documentNumberTemplate"
        :width="210"
      />
      <template #documentNumberTemplate="{ data }">
        <div>
          <span>{{ getDocumentNumberText(data.data) }}</span>
          <DxButton
            v-if="data.data.document_generation_error"
            icon="warning"
            :hint="$t('works.work_approval.document_generation_error')"
            styling-mode="text"
            type="danger"
            :on-click="() => showDocumentGenerationError(data.data)"
          />
        </div>
      </template>
      <DxColumn
        data-field="product_name"
        :caption="$t('works.work_approval.product_name')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="mu"
        :caption="$t('works.work_approval.mu')"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="total_quantity"
        :caption="$t('works.work_approval.total_quantity')"
        data-type="number"
        format="#,##0.####"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="dose_per_ha"
        :caption="$t('works.work_approval.dose_per_ha')"
        data-type="number"
        format="#,##0.####"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="cost_material"
        :caption="$t('works.work_approval.cost_material')"
        data-type="number"
        format="#,##0.##"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['contains']"
      />
      <DxPaging :page-size="pageSizes[0]" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxScrolling mode="standard" column-rendering-mode="standard" show-scrollbar="always" use-native="true" />
      <DxGroupPanel :visible="false" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxExport :enabled="false" :formats="['xlsx']" :allow-export-selected-data="false" :fileName="'WorkApprovalWorks'" />
    </DxDataGrid>

    <GridGroupActions :grid-ref="$refs.worksGrid" :selected-count="selectedWorkKeysAll.length" @cleared="clearSelection">
      <button
        v-if="canEdit"
        type="button"
        class="group-actions-button group-actions-button--primary"
        :disabled="!canApproveSelectedWorks() || isProcessing"
        @click="bulkApproveWorks"
      >
        {{ $t("works.work_approval.approve") }}
      </button>
      <button
        v-if="canEdit"
        type="button"
        class="group-actions-button group-actions-button--warning"
        :disabled="!canRevokeSelectedWorks() || isProcessing"
        @click="bulkRevokeWorks"
      >
        {{ $t("works.work_approval.revoke") }}
      </button>
    </GridGroupActions>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxLookup,
  DxPager,
  DxPaging,
  DxSelection,
  DxScrolling,
  DxGroupPanel,
  DxColumnChooser,
  DxExport,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import notify from "devextreme/ui/notify";
import DxButton from "devextreme-vue/button";
import GridGroupActions from "@/components/DxGrid/GridGroupActions.vue";
import { normalizeDateFilter } from "@/helpers/dateFilterHelper";
import { WORK_APPROVAL_STATUSES, WORK_APPROVAL_STATUS_OPTIONS, getWorkApprovalStatusOption } from "@/constants/workApprovalStatuses";

export default {
  name: "WorkApprovalWorksGrid",
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxLookup,
    DxPager,
    DxPaging,
    DxSelection,
    DxScrolling,
    DxGroupPanel,
    DxColumnChooser,
    DxExport,
    DxButton,
    GridGroupActions,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pageSizes: [15, 50, 100],
      gridRemoteOperations: { paging: true, filtering: true, sorting: true },
      gridAttributes: { class: "smagria-grid grid-offset-md" },
      isProcessing: false,
      selectedWorkKeys: [],
      selectedWorkKeysAll: [],
      worksGridDataUuids: [],
      worksGridRows: [],
      selectedWorkRowsByKey: {},
      isRestoringSelection: false,
      documentGenerationRefreshTimer: null,
      isDocumentGenerationStatusLoading: false,
      seasonsList: [],
      biggestYearUuid: null,
      workTypes: [],
      approvalOptions: WORK_APPROVAL_STATUS_OPTIONS.map((option) => ({
        value: option.value,
        name: this.$t(option.translationKey),
      })),
      rowTypeOptions: [
        { value: "spc", name: this.$t("common.work") },
        { value: "consum", name: this.$t("common.consum") },
      ],
      worksDataSource: new CustomStore({
        key: "id",
        load: (loadOptions) => this.loadWorks(loadOptions),
      }),
    };
  },
  methods: {
    ...mapActions({
      fetchWorks: "workApproval/getWorkApprovalWorks",
      doApproveWorks: "workApproval/approveWorks",
      doRevokeWorks: "workApproval/revokeWorks",
      fetchDocumentGenerationStatuses: "workApproval/getWorkApprovalConsumDocumentGenerationStatuses",
      getSeasons: "seasons/getSeasons",
      getWorkTypes: "works-manager/getWorks",
    }),
    async loadWorks(loadOptions) {
      const instance = this.$refs?.worksGrid?.instance;
      const pageLimit = loadOptions.isLoadingAll ? 99999 : instance?.pageSize?.() || loadOptions.take;
      const pageNum = (loadOptions.skip + pageLimit) / pageLimit;

      const payload = {
        page: pageNum,
        limit: pageLimit,
        date_from: this.filters.date_from ? this.toDateString(this.filters.date_from) : null,
        date_to: this.filters.date_to ? this.toDateString(this.filters.date_to) : null,
        filter: normalizeDateFilter(loadOptions.filter, "work_date"),
        sort: loadOptions.sort,
      };

      const response = await this.fetchWorks(payload);
      const rows = response?.data ?? [];
      this.worksGridDataUuids = rows.map((row) => row.id);
      this.worksGridRows = rows;
      return { data: rows, totalCount: response?.pagination?.total ?? rows.length };
    },
    async bulkApproveWorks() {
      if (!this.selectedWorkKeysAll.length) return;
      if (!this.canApproveSelectedWorks()) {
        this.notify(this.$t("works.work_approval.approve_invalid_selection"), "warning");
        return;
      }
      this.isProcessing = true;
      try {
        const { spcUuids, consumUuids } = this.splitWorkKeys(this.selectedWorkKeysAll);
        await this.doApproveWorks({ spc_uuids: spcUuids, consum_uuids: consumUuids });
        this.notify(this.$t("works.work_approval.approve_success"), "success");
        this.clearSelection();
        this.reload();
      } catch (error) {
        this.notify(error?.message || "Error", "error");
      } finally {
        this.isProcessing = false;
      }
    },
    async bulkRevokeWorks() {
      if (!this.selectedWorkKeysAll.length) return;
      if (!this.canRevokeSelectedWorks()) {
        this.notify(this.$t("works.work_approval.revoke_invalid_selection"), "warning");
        return;
      }
      this.isProcessing = true;
      try {
        const { spcUuids, consumUuids } = this.splitWorkKeys(this.selectedWorkKeysAll);
        await this.doRevokeWorks({ spc_uuids: spcUuids, consum_uuids: consumUuids });
        this.notify(this.$t("works.work_approval.revoke_success"), "success");
        this.clearSelection();
        this.reload();
      } catch (error) {
        this.notify(error?.message || "Error", "error");
      } finally {
        this.isProcessing = false;
      }
    },
    splitWorkKeys(keys) {
      const spcUuids = [];
      const consumUuids = [];
      keys.forEach((key) => {
        const [type, uuid] = key.split(":");
        if (type === "spc") spcUuids.push(uuid);
        if (type === "consum") consumUuids.push(uuid);
      });
      return { spcUuids, consumUuids };
    },
    getSeasonColumnText(rowData) {
      const seasonUuid = rowData?.season_uuid || rowData?.season?.uuid || null;
      if (!seasonUuid) {
        return rowData?.season_name || rowData?.season?.name || "-";
      }
      const season = this.seasonsList.find((item) => item.season_uuid === seasonUuid);
      return season?.name || rowData?.season_name || rowData?.season?.name || "-";
    },
    getRowTypeColumnText(rowData) {
      const rowType = rowData?.row_type || null;
      if (!rowType) {
        return "-";
      }
      const option = this.rowTypeOptions.find((item) => item.value === rowType);
      return option?.name || "-";
    },
    async loadSeasons() {
      try {
        const response = await this.getSeasons({ limit: 1000 });
        this.seasonsList = (response?.data || []).map((season) => ({
          season_uuid: season.uuid,
          name: season.name,
          year: season.year,
        }));

        const maxYear = Math.max(...this.seasonsList.map((item) => item.year).filter((year) => Number.isFinite(year)));
        this.biggestYearUuid = this.seasonsList.find((item) => item.year === maxYear)?.season_uuid || null;
        this.applyDefaultSeasonFilter();
      } catch (error) {
        this.seasonsList = [];
        this.biggestYearUuid = null;
      }
    },
    async loadWorkTypes() {
      try {
        const response = await this.getWorkTypes({ limit: 10000 });
        this.workTypes = response?.data || [];
      } catch (error) {
        this.workTypes = [];
      }
    },
    getWorkTypeColumnText(rowData) {
      const workUuid = rowData?.work_uuid || rowData?.work?.uuid || null;
      if (!workUuid) {
        return rowData?.work_name || "-";
      }
      const workType = this.workTypes.find((item) => item.uuid === workUuid);
      return workType?.name || rowData?.work_name || "-";
    },
    getApprovalStatusText(rowData) {
      const option = getWorkApprovalStatusOption(rowData?.approval_status);
      return this.$t(option.translationKey);
    },
    getApprovalStatusClass(rowData) {
      return getWorkApprovalStatusOption(rowData?.approval_status).className;
    },
    getDocumentNumberText(rowData) {
      if (rowData?.document_number) return rowData.document_number;
      if (["PENDING", "PROCESSING"].includes(rowData?.document_generation_status)) {
        return this.$t("works.work_approval.document_generation_in_progress");
      }
      return "-";
    },
    showDocumentGenerationError(rowData) {
      this.notify(rowData?.document_generation_error || "Error", "error");
    },
    canApproveSelectedWorks() {
      const rows = this.getSelectedWorkRows();
      return rows.length > 0 && rows.every((row) => row.approval_status !== WORK_APPROVAL_STATUSES.APPROVED);
    },
    canRevokeSelectedWorks() {
      const rows = this.getSelectedWorkRows();
      return rows.length > 0 && rows.every((row) => row.approval_status === WORK_APPROVAL_STATUSES.APPROVED);
    },
    getSelectedWorkRows() {
      return this.selectedWorkKeysAll
        .map((key) => this.selectedWorkRowsByKey[key] || this.worksGridRows.find((row) => row.id === key))
        .filter(Boolean);
    },
    applyDefaultSeasonFilter() {
      this.$nextTick(() => {
        const instance = this.$refs?.worksGrid?.instance;
        if (!instance || !this.biggestYearUuid) return;
        const currentFilter = instance.columnOption("season_uuid", "filterValue");
        if (currentFilter === undefined || currentFilter === null || currentFilter === "") {
          instance.columnOption("season_uuid", "filterValue", this.biggestYearUuid);
        }
      });
    },
    onSelectionChanged(e) {
      if (this.isRestoringSelection) return;
      const nextSelectedKeys = new Set(this.selectedWorkKeysAll);

      (e.currentSelectedRowKeys || []).forEach((key) => {
        this.getWorkSelectionGroupKeys(key).forEach((groupKey) => {
          nextSelectedKeys.add(groupKey);
          this.storeSelectedWorkRow(groupKey);
        });
      });
      (e.currentDeselectedRowKeys || []).forEach((key) => {
        this.getWorkSelectionGroupKeys(key).forEach((groupKey) => {
          nextSelectedKeys.delete(groupKey);
          this.$delete(this.selectedWorkRowsByKey, groupKey);
        });
      });

      this.selectedWorkKeysAll = Array.from(nextSelectedKeys);
      this.selectedWorkKeys = this.worksGridDataUuids.filter((key) => nextSelectedKeys.has(key));

      const currentSelectedKeys = Array.isArray(e.selectedRowKeys) ? e.selectedRowKeys : [];
      if (!this.sameKeys(currentSelectedKeys, this.selectedWorkKeys)) {
        this.isRestoringSelection = true;
        e.component.selectRows(this.selectedWorkKeys, false);
        this.$nextTick(() => {
          this.isRestoringSelection = false;
        });
      }
    },
    getWorkSelectionGroupKeys(key) {
      const row = this.worksGridRows.find((item) => item.id === key);
      if (!row || row.row_type !== "consum" || !row.consum_id) {
        return [key];
      }

      return this.worksGridRows
        .filter((item) => item.row_type === "consum" && item.consum_id === row.consum_id)
        .map((item) => item.id);
    },
    storeSelectedWorkRow(key) {
      const row = this.worksGridRows.find((item) => item.id === key);
      if (row) {
        this.$set(this.selectedWorkRowsByKey, key, row);
      }
    },
    sameKeys(firstKeys, secondKeys) {
      if (firstKeys.length !== secondKeys.length) return false;
      const secondSet = new Set(secondKeys);
      return firstKeys.every((key) => secondSet.has(key));
    },
    onRowClick(e) {
      if (!e || !e.component || e.rowType !== "data") return;
      const key = e.key;
      if (e.component.isRowSelected(key)) {
        e.component.deselectRows([key]);
      } else {
        e.component.selectRows([key], true);
      }
    },
    onContentReady() {
      const instance = this.$refs?.worksGrid?.instance;
      if (!instance) return;
      const items = instance.getDataSource()?.items?.() ?? [];
      this.worksGridDataUuids = items.map((row) => row.id);
      this.worksGridRows = items;
      this.refreshDocumentGenerationStatus(items);
      this.applyDefaultSeasonFilter();
      this.restoreSelection();
    },
    restoreSelection() {
      const instance = this.$refs?.worksGrid?.instance;
      if (!instance) return;
      this.isRestoringSelection = true;
      this.selectedWorkKeys = this.selectedWorkKeysAll.filter((key) => this.worksGridDataUuids.includes(key));
      instance.selectRows(this.selectedWorkKeys, false);
      this.$nextTick(() => {
        this.isRestoringSelection = false;
      });
    },
    clearSelection() {
      this.selectedWorkKeys = [];
      this.selectedWorkKeysAll = [];
      this.selectedWorkRowsByKey = {};
      this.$refs.worksGrid?.instance?.selectRows([], false);
    },
    onOptionChanged(e) {
      if (e?.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.worksGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
          instance.getDataSource()?.reload?.();
        }
      }
    },
    reload() {
      this.$refs.worksGrid?.instance?.getDataSource()?.reload?.();
    },
    refreshDocumentGenerationStatus(rows) {
      const pendingRows = rows.filter((row) => ["PENDING", "PROCESSING"].includes(row.document_generation_status));
      const hasPendingGeneration = pendingRows.length > 0;
      if (hasPendingGeneration && !this.documentGenerationRefreshTimer) {
        this.documentGenerationRefreshTimer = setInterval(() => {
          const currentRows = this.$refs.worksGrid?.instance?.getDataSource()?.items?.() || [];
          this.refreshDocumentGenerationStatus(currentRows);
        }, 5000);
      } else if (!hasPendingGeneration && this.documentGenerationRefreshTimer) {
        clearInterval(this.documentGenerationRefreshTimer);
        this.documentGenerationRefreshTimer = null;
      }

      if (hasPendingGeneration) {
        this.updateDocumentGenerationStatuses(pendingRows);
      }
    },
    async updateDocumentGenerationStatuses(pendingRows) {
      if (this.isDocumentGenerationStatusLoading) return;
      this.isDocumentGenerationStatusLoading = true;
      try {
        const statuses = await this.fetchDocumentGenerationStatuses({
          uuids: pendingRows.map((row) => row.uuid),
        });
        this.applyDocumentGenerationStatuses(statuses || []);
      } catch (error) {
        // The next polling interval retries transient status endpoint failures.
      } finally {
        this.isDocumentGenerationStatusLoading = false;
      }
    },
    applyDocumentGenerationStatuses(statuses) {
      const statusesByUuid = new Map(statuses.map((status) => [status.uuid, status]));
      const instance = this.$refs.worksGrid?.instance;
      const rows = instance?.getDataSource()?.items?.() || [];
      const changedRowIndexes = [];

      rows.forEach((row, index) => {
        const status = statusesByUuid.get(row.uuid);
        if (!status) return;
        const hasChanges = ["document_number", "document_generation_status", "document_generation_error"]
          .some((field) => row[field] !== status[field]);
        if (!hasChanges) return;

        Object.assign(row, status);
        Object.assign(this.worksGridRows.find((item) => item.id === row.id) || {}, status);
        Object.assign(this.selectedWorkRowsByKey[row.id] || {}, status);
        changedRowIndexes.push(index);
      });

      if (changedRowIndexes.length) {
        instance?.repaintRows(changedRowIndexes);
        this.refreshDocumentGenerationStatus(rows);
      }
    },
    activate() {
      this.$nextTick(() => {
        const instance = this.$refs?.worksGrid?.instance;
        if (!instance) return;
        if (typeof instance.repaint === "function") {
          instance.repaint();
        }
        if (typeof instance.updateDimensions === "function") {
          instance.updateDimensions();
        }
      });
    },
    openColumnChooser() {
      const instance = this.$refs?.worksGrid?.instance;
      if (instance && typeof instance.showColumnChooser === "function") {
        instance.showColumnChooser();
      }
    },
    exportGrid() {
      const instance = this.$refs?.worksGrid?.instance;
      if (!instance) return;

      this.onExporting({
        component: instance,
        format: "xlsx",
        fileName: "WorkApprovalWorks_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss"),
      });
    },
    onExporting(e) {
      e.fileName = e.fileName || "WorkApprovalWorks_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("WorkApprovalWorks");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (gridCell.rowType !== "data") {
              return;
            }

            const columnField = gridCell.column?.dataField;

            if (columnField === "season_uuid") {
              excelCell.value = this.getSeasonColumnText(gridCell.data);
              return;
            }

            if (columnField === "row_type") {
              excelCell.value = this.getRowTypeColumnText(gridCell.data);
              return;
            }

            if (columnField === "work_uuid") {
              excelCell.value = this.getWorkTypeColumnText(gridCell.data);
              return;
            }

            if (columnField === "approval_status") {
              excelCell.value = this.getApprovalStatusText(gridCell.data);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    notify(message, type) {
      notify({ message, width: "auto", position: { at: "center", my: "start", of: "#toast-msg" } }, type, 3000);
    },
    toDateString(dateValue) {
      if (!dateValue) return null;
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
  },
  mounted() {
    this.loadWorkTypes();
    this.loadSeasons();
    this.activate();
  },
  beforeDestroy() {
    clearInterval(this.documentGenerationRefreshTimer);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/workApprovalGrid.scss";
</style>
